import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, authConfig, formatApiErrorDetail } from "@/lib/api";
import { buildAgentProfileFormState, AgentProfileSetupForm } from "@/components/agent/AgentProfileSetupForm";

export const AgentProfileSettingsSheet = ({ onProfileSaved, open, setOpen, user }) => {
  const [form, setForm] = useState(() => buildAgentProfileFormState(user));
  const [busy, setBusy] = useState(false);
  const [syncingNow, setSyncingNow] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(buildAgentProfileFormState(user));
      api.get("/agent/profile-settings").then((response) => {
        setForm(buildAgentProfileFormState(response.data || user));
      }).catch(async (error) => {
        if (error?.response?.status === 404) {
          try {
            const response = await api.get("/onboarding/wizard");
            setForm(buildAgentProfileFormState(response.data || user));
            return;
          } catch {
            // Keep fallback form state from session user if fallback load fails.
          }
        }
        // Keep fallback form state from session user if fetch fails.
      });
    }
  }, [open, user]);

  const portalPreview = useMemo(() => (form.subdomain_slug ? `/agent/${String(form.subdomain_slug).trim()}` : "/agent/your-name"), [form.subdomain_slug]);

  const updateField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const uploadHeadshot = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        toast.error("Only JPG, PNG, and WEBP files are supported");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Image must be 10MB or smaller");
        return;
      }
      try {
        setBusy(true);
        const signaturePayload = await api.get("/cloudinary/signature", authConfig({ params: { resource_type: "image", folder: "uploads/agents/headshots" } }));
        const formData = new FormData();
        formData.append("file", file);
        formData.append("api_key", signaturePayload.data.api_key);
        formData.append("timestamp", String(signaturePayload.data.timestamp));
        formData.append("signature", signaturePayload.data.signature);
        formData.append("folder", signaturePayload.data.folder);
        const response = await fetch(`https://api.cloudinary.com/v1_1/${signaturePayload.data.cloud_name}/image/upload`, {
          method: "POST",
          body: formData,
        });
        const asset = await response.json();
        if (!response.ok) {
          throw new Error(asset.error?.message || "Cloudinary upload failed");
        }
        const optimizedUrl = String(asset.secure_url || "").replace("/upload/", "/upload/f_auto,q_auto,w_1200,c_limit/");
        setForm((current) => ({ ...current, profile_photo_url: optimizedUrl }));
        toast.success("Headshot uploaded");
      } catch (error) {
        toast.error(error?.message || formatApiErrorDetail(error?.response?.data?.detail) || "Unable to upload headshot");
      } finally {
        setBusy(false);
      }
    };
    input.click();
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    try {
      setBusy(true);
      let response;
      try {
        response = await api.put("/agent/profile-settings", form);
      } catch (error) {
        if (error?.response?.status !== 404) throw error;
        response = await api.put("/onboarding/wizard", {
          ...form,
          specialties: [],
          service_regions: [],
          preferred_categories: ["buy"],
          response_sla: "Within 2 hours",
          onboarding_step: 1,
        });
      }
      toast.success("Profile settings saved");
      onProfileSaved?.(response.data);
      setOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to save profile settings");
    } finally {
      setBusy(false);
    }
  };

  const runXmlSyncNow = async () => {
    try {
      setSyncingNow(true);
      const response = await api.post("/agent/xml-syndication/sync-now");
      toast.success(`XML sync complete: ${response.data.synced_count || 0} listing${response.data.synced_count === 1 ? "" : "s"} processed.`);
      const refreshed = await api.get("/agent/profile-settings");
      setForm(buildAgentProfileFormState(refreshed.data || user));
      onProfileSaved?.(refreshed.data || user);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to run XML sync now"));
    } finally {
      setSyncingNow(false);
    }
  };

  return (
    <Sheet onOpenChange={setOpen} open={open}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="agent-profile-settings-sheet" side="right">
        <SheetHeader>
          <SheetTitle data-testid="agent-profile-settings-sheet-title">Profile Settings</SheetTitle>
        </SheetHeader>
        <form className="mt-6" onSubmit={saveProfile}>
          <AgentProfileSetupForm
            busy={busy}
            ctaLabel="Save Profile Settings"
            description="Update your compliance and branding details here. Changes sync directly to your live public portal route."
            form={form}
            onChange={updateField}
            onUploadHeadshot={uploadHeadshot}
            portalPreview={portalPreview}
            title="Manage your live agent profile"
          />
          <div className="mt-5 rounded-[24px] border border-[#001935]/10 bg-[#fcfaf5] p-4" data-testid="agent-xml-syndication-status-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-[#001935]">External Feed Sync Status</p>
                <p className="mt-1 text-xs leading-6 text-[#6b7280]" data-testid="agent-xml-syndication-status-copy">
                  Status: {form.xml_sync_last_status || "not_configured"} {form.xml_sync_last_run_at ? `• Last run: ${form.xml_sync_last_run_at}` : ""}
                </p>
                {form.xml_sync_last_error ? <p className="mt-2 text-xs text-red-600" data-testid="agent-xml-syndication-error-copy">{form.xml_sync_last_error}</p> : null}
              </div>
              <button className="outline-button" data-testid="agent-xml-syndication-sync-now-button" disabled={syncingNow || !form.xml_syndication_url || busy} onClick={runXmlSyncNow} type="button">
                {syncingNow ? "Syncing…" : "Sync Now"}
              </button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default AgentProfileSettingsSheet;